import { SEARCH_TYPE } from '@/constants/searchType';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import { slate } from '@/lib/colours';
import {
  type EbvConfigItem,
  isDisplayableEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { formatEbvAndIndexesTabLabel } from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import { lookupsWithI6Mapping } from './expandI6LookupFilters';
import { FieldOption, MemberFilterField, SaleType } from '../constants';

export type { EbvConfigItem };

/** Shared by results pill (`SearchCountPillButton`) and saved-filters chevron in `PublicSearchInput`. */
export const publicSearchEndAdornmentHeightPx = 28;

/** Outlined field border for quick-search and mating-bar controls (light + dark). */
export const quickSearchOutlinedBorder = `1.5px solid ${slate[300]}`;

/** Shared sx for quick-search-style inputs (hero search, mating predictor, etc.). Merge with local sx. */
export const quickSearchInputSx = {
  '& .MuiInputBase-root': {
    height: '38px',
    borderRadius: '8px',
    backgroundColor: 'background.paper',
    fontSize: '13px',
    '& fieldset': { border: quickSearchOutlinedBorder },
  },
};

export const CatalogueDetailAnimalsOptions = [
  FieldOption.animalIdent,
  FieldOption.animalName,
  FieldOption.animalStatus,
  FieldOption.dateOfBirth,
  FieldOption.sex,
  FieldOption.registrationStatus,
  FieldOption.calvingYear,
  FieldOption.saleCount,
  FieldOption.sire,
  FieldOption.dam,
  FieldOption.breeder,
  FieldOption.currentOwner,
  FieldOption.tattoo,
];

export const SaleCatalogueSelectOptions = [
  FieldOption.saleName,
  FieldOption.saleType,
  FieldOption.saleDate,
];

/**
 * Input control rendered for a filter value. `dropdown` is a custom value
 * handled by our components; the rest are native HTML input types passed
 * straight through to the underlying `TextField`.
 */
export const FILTER_INPUT_TYPE = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  DROPDOWN: 'dropdown',
} as const;

/**
 * Field control type as configured in a society's search-field settings
 * (the `type` property of ANIMAL_SEARCH_FIELDS / MEMBER_SEARCH_FIELDS JSON
 * entries). `select` and `radio` are lookup-driven; `text` is the default.
 */
const FIELD_SETTING_TYPE = {
  TEXT: 'text',
  SELECT: 'select',
  RADIO: 'radio',
} as const;

type FieldSettingType =
  (typeof FIELD_SETTING_TYPE)[keyof typeof FIELD_SETTING_TYPE];

export const InputTypeByField = {
  [FieldOption.animalStatus.value]: FILTER_INPUT_TYPE.DROPDOWN,
  [FieldOption.calvingYear.value]: FILTER_INPUT_TYPE.TEXT,
  [FieldOption.saleCount.value]: FILTER_INPUT_TYPE.NUMBER,
  [FieldOption.dateOfBirth.value]: FILTER_INPUT_TYPE.DATE,
  [FieldOption.member.value]: FILTER_INPUT_TYPE.TEXT,
  [FieldOption.saleDate.value]: FILTER_INPUT_TYPE.DATE,
  [FieldOption.saleType.value]: FILTER_INPUT_TYPE.DROPDOWN,
  [FieldOption.sex.value]: FILTER_INPUT_TYPE.DROPDOWN,
} as const;

const AnimalStatusOption = {
  Active: { label: 'Active', value: 'Active' },
  Dead: { label: 'Dead', value: 'Dead' },
  Inactive: { label: 'Inactive', value: 'Inactive' },
  Sold: { label: 'Sold', value: 'Sold' },
  Unknown: { label: 'Unknown', value: 'Unknown' },
};

const SexOption = {
  Male: { label: 'Male', value: 'M' },
  Female: { label: 'Female', value: 'F' },
  Steer: { label: 'Steer', value: 'S' },
  Unknown: { label: 'Unknown', value: 'U' },
};

const SaleTypeOption = {
  Sales: { label: 'Sales', value: SaleType.CurrentSaleListing },
  PrivateTreaty: {
    label: 'Private Treaty',
    value: SaleType.CurrentPrivateTreatySaleListing,
  },
  Semen: { label: 'Semen', value: SaleType.SemenListing },
};

// Sale Type options for combined Sale Catalogues (list + advanced filter)
export const CatalogueSaleTypeOption = {
  All: { label: 'All', value: '' },
  Sales: { label: 'Sales', value: SaleType.CurrentSaleListing },
  PrivateTreaty: {
    label: 'Private Treaty',
    value: SaleType.CurrentPrivateTreatySaleListing,
  },
  Semen: { label: 'Semen', value: SaleType.SemenListing },
  Embryo: { label: 'Embryo', value: SaleType.EmbryoListing },
};

// Legacy: Sale Type options for Sale Catalogues (advanced filter only)
const SaleCatalogueSaleTypeOption = {
  Sales: { label: 'Sales', value: SaleType.CurrentSaleListing },
  PrivateTreaty: {
    label: 'Private Treaty',
    value: SaleType.CurrentPrivateTreatySaleListing,
  },
  Semen: { label: 'Semen', value: SaleType.SemenListing },
  Embryo: { label: 'Embryo', value: SaleType.EmbryoListing },
};

export const OptionsByField = {
  [FieldOption.animalStatus.value]: Object.values(AnimalStatusOption),
  [FieldOption.saleType.value]: Object.values(SaleTypeOption), // Default fallback
  [FieldOption.sex.value]: Object.values(SexOption),
};

// Get options for a field based on search type
export function getOptionsByFieldAndSearchType(
  field: string,
  searchType: string,
) {
  // For sale type field, use unified catalogue options when on sale-catalogues
  if (field === FieldOption.saleType.value) {
    if (searchType === SEARCH_TYPE.SALE_CATALOGUES) {
      return Object.values(SaleCatalogueSaleTypeOption);
    }
  }

  // Default to OptionsByField for other fields
  return OptionsByField[field] || [];
}

export const BasicSearchAnimalFields = [
  FieldOption.searchTerm,
  FieldOption.sex,
  FieldOption.registrationStatus,
  FieldOption.dateOfBirth,
  {
    ...FieldOption.sire,
    label: 'Sire',
  },
  {
    ...FieldOption.dam,
    label: 'Dam',
  },
  FieldOption.calvingYear,
  FieldOption.currentOwner,
  FieldOption.breeder,
  FieldOption.tattoo,
];

export const BasicSearchDetailAnimalFields = [...BasicSearchAnimalFields];

const BasicSearchMemberFields = [
  { ...FieldOption.member, label: 'Member' },
  FieldOption.membershipType,
];

export type SettingsArray =
  | Array<{ societySettingType?: string | null; value?: string | null }>
  | null
  | undefined;

const SETTING_FIELD_TO_FIELD_OPTION: Record<
  string,
  { key?: string; label: string; value: string }
> = {
  SearchTerm: FieldOption.searchTerm,
  Animal: FieldOption.searchTerm,
  Sex: FieldOption.sex,
  RegistrationStatus: FieldOption.registrationStatus,
  DateOfBirth: FieldOption.dateOfBirth,
  Sire: FieldOption.sire,
  Dam: FieldOption.dam,
  CalvingYear: FieldOption.calvingYear,
  CurrentOwner: FieldOption.currentOwner,
  Breeder: FieldOption.breeder,
  Tattoo: FieldOption.tattoo,
  AnimalTattoo: FieldOption.tattoo,
  AnimalIdent: FieldOption.animalIdent,
  AnimalName: FieldOption.animalName,
  AnimalStatus: FieldOption.animalStatus,
  Colour: FieldOption.colour,
};

export type AnimalSearchFieldLookup = {
  code: string;
  description: string;
  /** Cleaned label from RPT (HTML stripped); prefer over description when set. */
  i6LookupDescription?: string | null;
  i6LookupString?: string;
  /** Absent for literal legacy i4 values; set by the BL options publisher. */
  i6LookupValueEncoding?: 'json';
};

/** Strip legacy i4 HTML (e.g. Super Sires &lt;img…&gt;) from lookup option labels. */
export function lookupOptionLabel(lookup: AnimalSearchFieldLookup): string {
  const cleaned = lookup.i6LookupDescription?.trim();
  if (cleaned) return cleaned;
  return (lookup.description ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

type AnimalSearchFieldSettingItem = {
  field: string;
  label: string;
  type: string;
  order: string;
  lookupKey?: string;
  lookups?: AnimalSearchFieldLookup[];
};

export type BasicSearchField = {
  key?: string;
  label: string;
  value: string;
  settingType?: FieldSettingType;
  lookups?: AnimalSearchFieldLookup[];
};

/**
 * Parse ANIMAL_SEARCH_FIELDS_EXCLUDE from society settings.
 * Returns a Set of field names (matching SETTING_FIELD_TO_FIELD_OPTION keys)
 * that should be hidden from the basic search.
 *
 * The setting value is a JSON array of strings, e.g.
 *   ["AnimalName","AnimalIdent","RegistrationStatus"]
 */
function parseAnimalSearchFieldsExclude(settings: SettingsArray): Set<string> {
  if (!settings || !Array.isArray(settings)) return new Set();

  const setting = settings.find(
    (s) =>
      s &&
      s.societySettingType ===
        SOCIETY_SETTING_TYPE.ANIMAL_SEARCH_FIELDS_EXCLUDE,
  );
  if (!setting?.value) return new Set();

  try {
    const parsed = JSON.parse(setting.value);
    if (!Array.isArray(parsed)) return new Set();

    const excludeValues = new Set<string>();
    for (const name of parsed) {
      if (typeof name !== 'string') continue;
      const mapped = SETTING_FIELD_TO_FIELD_OPTION[name];
      if (mapped) {
        excludeValues.add(mapped.value);
      } else {
        excludeValues.add(name);
      }
    }
    return excludeValues;
  } catch {
    return new Set();
  }
}

type FieldOptionMap = Record<
  string,
  { key?: string; label: string; value: string }
>;

/**
 * Shared parse/sort/map skeleton for ANIMAL_SEARCH_FIELDS and
 * MEMBER_SEARCH_FIELDS settings. Both produce BasicSearchField[] from a
 * society setting JSON array; they differ only in the setting type, the
 * field-mapping table, and how an unmapped lookup field's fallback
 * label/value are derived (animal strips known prefixes and retries the
 * map; member uses the raw field name).
 */
function parseSearchFieldsSetting(
  settings: SettingsArray,
  societySettingType: string,
  fieldOptionMap: FieldOptionMap,
  /**
   * Compute the fallback `{ label, value }` for a lookup field that has no
   * direct entry in `fieldOptionMap`. `label` is used only when the item
   * has no explicit label of its own.
   */
  resolveLookupFallback: (field: string) => { label: string; value: string },
): BasicSearchField[] {
  if (!settings || !Array.isArray(settings)) return [];

  const setting = settings.find(
    (candidate) =>
      candidate && candidate.societySettingType === societySettingType,
  );
  if (!setting?.value) return [];

  try {
    const parsed = JSON.parse(setting.value) as AnimalSearchFieldSettingItem[];
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item) => item && item.field)
      .sort(
        (a, b) => parseInt(String(a.order), 10) - parseInt(String(b.order), 10),
      )
      .map((item): BasicSearchField | null => {
        const label = item.label
          ? item.label.replace(/\^/g, ' ').replace(/:$/, '')
          : '';
        const isChoiceField =
          item.type === FIELD_SETTING_TYPE.SELECT ||
          item.type === FIELD_SETTING_TYPE.RADIO;
        const lookups = isChoiceField
          ? lookupsWithI6Mapping(item.lookups)
          : item.lookups;
        if (isChoiceField && !lookups?.length) return null;

        const base = fieldOptionMap[item.field];
        if (base) {
          return {
            ...base,
            ...(lookups?.length ? { key: item.field } : {}),
            label: label || base.label,
            settingType:
              (item.type as BasicSearchField['settingType']) ||
              FIELD_SETTING_TYPE.TEXT,
            ...(lookups?.length ? { lookups } : {}),
          };
        }

        if (isChoiceField && lookups?.length) {
          const fallback = resolveLookupFallback(item.field);
          return {
            key: item.field,
            label: label || fallback.label,
            value: fallback.value,
            settingType: item.type as FieldSettingType,
            lookups,
          };
        }

        // Society custom overflow fields are plain-text search fields that are NOT in the
        // fixed fieldOptionMap. They were previously dropped, so a configured column like
        // "ROBT" (SocietyAnimalField5) never appeared as a search option. Surface them so
        // basic search matches the columns / advanced-filter dropdown:
        //   • Animal: keep the setting token (SocietyAnimalFieldN); the animal filter builder's
        //     transformFieldName translates it -> SOCIETY_ANIMAL_FIELDN (AnimalEbvFilterField).
        //   • Member: emit the SOC_ME_FIELD{n} UI token (same value the advanced-filter dropdown
        //     uses via getMemberFilterFieldOptionsFromSearchSettings) so both paths agree;
        //     buildMemberFilterGroups' mapMemberSearchFields translates it -> SOCIETY_MEMBER_FIELDN
        //     (MemberFilterField) at GraphQL build time.
        if (/^SocietyAnimalField\d+$/i.test(item.field)) {
          return {
            key: item.field,
            label: label || item.field,
            value: item.field,
            settingType: FIELD_SETTING_TYPE.TEXT,
          };
        }

        const memberFieldMatch = item.field.match(/^SocietyMemberField(\d+)$/i);
        if (memberFieldMatch) {
          return {
            key: item.field,
            label: label || item.field,
            value: `SOC_ME_FIELD${memberFieldMatch[1]}`,
            settingType: FIELD_SETTING_TYPE.TEXT,
          };
        }

        return null;
      })
      .filter((f): f is BasicSearchField => f !== null);
  } catch {
    return [];
  }
}

/**
 * Parse ANIMAL_SEARCH_FIELDS from society settings and map each entry to
 * a field descriptor compatible with BasicSearchCondition.
 *
 * Standard fields (AnimalName, Sire, etc.) map to FieldOption entries.
 * Custom fields (as_select_*, as_grp_select_*, etc.) with lookups are
 * returned as dropdown fields whose options come from the lookup list.
 */
function parseAnimalSearchFieldsSetting(
  settings: SettingsArray,
): BasicSearchField[] {
  return parseSearchFieldsSetting(
    settings,
    SOCIETY_SETTING_TYPE.ANIMAL_SEARCH_FIELDS,
    SETTING_FIELD_TO_FIELD_OPTION,
    (field) => {
      // Strip known prefixes (as_select_*, as_grp_select_*) and retry
      const stripped = field.replace(/^(as_select_|as_grp_select_)/, '');
      const mappedBase =
        stripped !== field
          ? SETTING_FIELD_TO_FIELD_OPTION[stripped]
          : undefined;
      return {
        label: mappedBase?.label ?? field,
        value: mappedBase?.value ?? field,
      };
    },
  );
}

/** Same marker prefix as member search lookup pills. */
export const ANIMAL_LOOKUP_FIELD_PREFIX = '__lookup__';

/** The same identity used by the basic control and the query lookup resolver. */
export function getSearchFieldIdentity(field: BasicSearchField): string {
  return field.lookups?.length
    ? `${ANIMAL_LOOKUP_FIELD_PREFIX}${field.key ?? field.value}`
    : field.value;
}

/** Merge useful scalar defaults with society controls without duplicating an identity. */
function mergeBasicSearchFields(
  defaults: BasicSearchField[],
  configured: BasicSearchField[],
  configuredOnlyValues: Set<string>,
): BasicSearchField[] {
  const byValue = new Map(configured.map((field) => [field.value, field]));
  const byLabel = new Map(
    configured.map((field) => [field.label.toLowerCase().trim(), field]),
  );
  const merged = defaults
    .filter(
      (field) =>
        !configuredOnlyValues.has(field.value) || byValue.has(field.value),
    )
    .map(
      (field) =>
        byValue.get(field.value) ??
        byLabel.get(field.label.toLowerCase().trim()) ??
        field,
    );
  const seen = new Set<string>();
  return [...merged, ...configured].filter((field) => {
    const identity = getSearchFieldIdentity(field);
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}

/** Shared availability for basic search, advanced search and AI search. */
export function getAnimalBasicSearchFields(
  settings: SettingsArray,
  defaults: BasicSearchField[] = BasicSearchAnimalFields,
): BasicSearchField[] {
  const excluded = parseAnimalSearchFieldsExclude(settings);
  return mergeBasicSearchFields(
    defaults,
    parseAnimalSearchFieldsSetting(settings),
    new Set([FieldOption.registrationStatus.value]),
  ).filter(
    (field) =>
      !excluded.has(field.value) &&
      !excluded.has(getSearchFieldIdentity(field)),
  );
}

export function getMemberBasicSearchFields(
  settings: SettingsArray,
): BasicSearchField[] {
  return mergeBasicSearchFields(
    BasicSearchMemberFields,
    parseMemberSearchFieldsSetting(settings),
    new Set([FieldOption.membershipType.value]),
  );
}

function getAnimalFilterFieldOptionsFromSearchSettings(
  settings: SettingsArray,
): DynamicFieldOption[] {
  return getAnimalBasicSearchFields(settings).map((field) => ({
    label: field.label,
    value: getSearchFieldIdentity(field),
    lookups: field.lookups,
  }));
}

function parseAnimalSearchFieldsRaw(
  settings: SettingsArray,
): AnimalSearchFieldSettingItem[] | null {
  if (!settings || !Array.isArray(settings)) return null;

  const setting = settings.find(
    (s) =>
      s && s.societySettingType === SOCIETY_SETTING_TYPE.ANIMAL_SEARCH_FIELDS,
  );
  if (!setting?.value) return null;

  try {
    const parsed = JSON.parse(setting.value);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (item): item is AnimalSearchFieldSettingItem =>
        item != null &&
        typeof item === 'object' &&
        typeof (item as AnimalSearchFieldSettingItem).field === 'string',
    );
  } catch {
    return null;
  }
}

/**
 * Build a resolver map for animal lookup pills (`__lookup__as_grp_select_*`, etc.)
 * from ANIMAL_SEARCH_FIELDS (SocietySettingType = 17).
 */
export function getAnimalLookupResolverFromSearchSettings(
  settings: SettingsArray,
): Map<string, AnimalSearchFieldLookup[]> {
  const map = new Map<string, AnimalSearchFieldLookup[]>();
  const items = parseAnimalSearchFieldsRaw(settings);
  if (!items) return map;

  for (const item of items) {
    const fieldName = String(item.field);
    const itemType = typeof item.type === 'string' ? item.type : '';
    const lookups = Array.isArray(item.lookups) ? item.lookups : [];
    if (
      (itemType === FIELD_SETTING_TYPE.SELECT ||
        itemType === FIELD_SETTING_TYPE.RADIO) &&
      lookups.length > 0
    ) {
      map.set(`${ANIMAL_LOOKUP_FIELD_PREFIX}${fieldName}`, lookups);
    }
  }

  return map;
}

const MEMBER_SETTING_FIELD_TO_FIELD_OPTION: Record<
  string,
  { key?: string; label: string; value: string }
> = {
  Member: FieldOption.member,
  MemberName: FieldOption.memberName,
  MembName: FieldOption.memberName,
  MemberIdent: FieldOption.memberIdent,
  MembershipType: FieldOption.membershipType,
  MemberType: FieldOption.membershipType,
  HerdPrefix: FieldOption.herdPrefix,
  Prefix: FieldOption.herdPrefix,
  MemberId: FieldOption.memberId,
};

/**
 * MEMBER_SEARCH_FIELDS / MEMBER_LIST_FIELDS use CamelCase setting field
 * names; map each one to the matching MemberFilterField filter enum so the
 * society-customised label (e.g. "Membership Notes") can drive the filter
 * dropdown. SocietyMemberField{n} → SOC_ME_FIELD{n} is handled dynamically
 * (regex) in `memberSearchFieldNameToFilterEnum` rather than enumerated.
 */
const MEMBER_LIST_SETTING_FIELD_TO_FILTER_FIELD: Record<
  string,
  MemberFilterField
> = {
  MemberId: MemberFilterField.MEMBER_ID,
  MemberIdent: MemberFilterField.MEMBER_IDENT,
  MembName: MemberFilterField.MEMB_NAME,
  MemberName: MemberFilterField.MEMB_NAME,
  Prefix: MemberFilterField.PREFIX,
  HerdPrefix: MemberFilterField.PREFIX,
  MemberType: MemberFilterField.MEMBER_TYPE,
  MembershipType: MemberFilterField.MEMBER_TYPE,
  Postcode: MemberFilterField.POSTCODE,
  Address1: MemberFilterField.ADDRESS1,
  Address2: MemberFilterField.ADDRESS2,
  Address3: MemberFilterField.ADDRESS3,
  Sortname: MemberFilterField.SORTNAME,
  Email: MemberFilterField.EMAIL,
  PhoneDetails: MemberFilterField.PHONE_DETAILS,
  Homepage: MemberFilterField.HOMEPAGE,
  HerdSize: MemberFilterField.HERD_SIZE,
  HerdSizeDesc: MemberFilterField.HERD_SIZE_DESC,
  MembStatus: MemberFilterField.MEMB_STATUS,
  MemberStatus: MemberFilterField.MEMB_STATUS,
  MemberNotes: MemberFilterField.MEMBER_NOTES,
  RenewalDate: MemberFilterField.RENEWAL_DATE,
};

/**
 * Marker prefix used on the pill `field` so the UI / build pipeline can tell
 * "this pill represents a society-defined lookup choice" apart from a
 * regular MemberFilterField pill. Matches the convention already in use by
 * BasicSearchLookupCondition.
 */
export const MEMBER_LOOKUP_FIELD_PREFIX = '__lookup__';

/**
 * Map a MEMBER_SEARCH_FIELDS field name to a MemberFilterField enum value.
 *
 *   • SocietyMemberField{n}              → SOC_ME_FIELD{n}
 *   • PascalCase standard names           → corresponding enum
 *   • ms_* / state_grp_select / radio     → null (lookup-driven; no scalar
 *                                            MemberFilterField equivalent)
 */
function memberSearchFieldNameToFilterEnum(
  fieldName: string,
): MemberFilterField | string | null {
  const socMatch = fieldName.match(/^SocietyMemberField(\d+)$/i);
  if (socMatch) return `SOC_ME_FIELD${socMatch[1]}`;
  return MEMBER_LIST_SETTING_FIELD_TO_FILTER_FIELD[fieldName] ?? null;
}

type MemberSearchFieldRawItem = {
  field?: unknown;
  label?: unknown;
  type?: unknown;
  order?: unknown;
  lookups?: unknown;
};

function parseMemberSearchFieldsRaw(
  settings: SettingsArray,
): MemberSearchFieldRawItem[] | null {
  if (!settings || !Array.isArray(settings)) return null;
  const setting = settings.find(
    (s) =>
      s && s.societySettingType === SOCIETY_SETTING_TYPE.MEMBER_SEARCH_FIELDS,
  );
  if (!setting?.value) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(setting.value);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const items = parsed.filter(
    (item): item is MemberSearchFieldRawItem =>
      item != null &&
      typeof item === 'object' &&
      typeof (item as MemberSearchFieldRawItem).field === 'string',
  );
  items.sort(
    (a, b) =>
      parseInt(String(a.order ?? '0'), 10) -
      parseInt(String(b.order ?? '0'), 10),
  );
  return items;
}

/**
 * Read MEMBER_SEARCH_FIELDS (`SocietySettingType = 19`) and return one
 * DynamicFieldOption per entry — in the order the society configured them.
 * Returns `null` when the setting is absent so callers can fall back.
 *
 * MEMBER_SEARCH_FIELDS is the right source for the filter dropdown because
 * it is the search-specific config (MS_Col / MS_Head) — it tells us which
 * fields the society wants exposed for member search and what they should
 * be called there. Mirrors how animal uses ANIMAL_SEARCH_FIELDS to drive
 * its basic-search field list.
 *
 * Three flavours of entry are mapped:
 *   • Standard scalar fields → `{value: <MemberFilterField enum>}`
 *   • SocietyMemberField{n}   → `{value: SOC_ME_FIELD{n}}`
 *   • Lookup-driven (ms_*, state_grp_select, *_radio) with `lookups` →
 *     `{value: __lookup__<field>, lookups: […]}`. The pill renders a
 *     description dropdown; the build pipeline expands the selected
 *     lookup's `i6LookupString` into the real underlying filter(s).
 */
function getMemberFilterFieldOptionsFromSearchSettings(
  settings: SettingsArray,
): DynamicFieldOption[] | null {
  const items = parseMemberSearchFieldsRaw(settings);
  if (items === null) return null;

  const result: DynamicFieldOption[] = [];
  const seen = new Set<string>();
  // Distinct configured identities remain addressable even when their labels
  // match, so saved/basic selections also have labels and choices in Advanced.

  for (const item of items) {
    const fieldName = String(item.field);
    const rawLabel = typeof item.label === 'string' ? item.label : '';
    const cleanLabel = rawLabel.replace(/\^/g, ' ').replace(/:$/, '').trim();
    const itemType = typeof item.type === 'string' ? item.type : '';
    const lookups = lookupsWithI6Mapping(
      Array.isArray(item.lookups)
        ? (item.lookups as AnimalSearchFieldLookup[])
        : [],
    );

    const isLookup =
      itemType === FIELD_SETTING_TYPE.SELECT ||
      itemType === FIELD_SETTING_TYPE.RADIO;

    if (isLookup) {
      if (!lookups.length) continue;
      const value = `${MEMBER_LOOKUP_FIELD_PREFIX}${fieldName}`;
      if (seen.has(value)) continue;
      seen.add(value);
      result.push({
        label: cleanLabel || fieldName,
        value,
        lookups,
      });
      continue;
    }

    const mapped = memberSearchFieldNameToFilterEnum(fieldName);
    if (!mapped || seen.has(mapped)) continue;
    seen.add(mapped);
    result.push({
      label: cleanLabel || (MEMBER_FILTER_FIELD_LABELS[mapped] ?? mapped),
      value: mapped,
    });
  }

  return result;
}

/**
 * Build a `Map<lookupPillField, AnimalSearchFieldLookup[]>` for resolving
 * member lookup pills (those with `field` like `__lookup__ms_grp_select_1`)
 * into their concrete underlying filter at GraphQL-build time.
 *
 * Returns an empty map when MEMBER_SEARCH_FIELDS is absent — callers should
 * still defensively skip lookup-marker pills they can't resolve.
 */
export function getMemberLookupResolverFromSearchSettings(
  settings: SettingsArray,
): Map<string, AnimalSearchFieldLookup[]> {
  const map = new Map<string, AnimalSearchFieldLookup[]>();
  const items = parseMemberSearchFieldsRaw(settings);
  if (!items) return map;
  for (const item of items) {
    const fieldName = String(item.field);
    const itemType = typeof item.type === 'string' ? item.type : '';
    const lookups = Array.isArray(item.lookups)
      ? (item.lookups as AnimalSearchFieldLookup[])
      : [];
    if (
      (itemType === FIELD_SETTING_TYPE.SELECT ||
        itemType === FIELD_SETTING_TYPE.RADIO) &&
      lookups.length > 0
    ) {
      map.set(`${MEMBER_LOOKUP_FIELD_PREFIX}${fieldName}`, lookups);
    }
  }
  return map;
}

/**
 * Parse MEMBER_SEARCH_FIELDS from society settings and map each entry to
 * a field descriptor compatible with BasicSearchCondition.
 *
 * Same pattern as parseAnimalSearchFieldsSetting but for members.
 */
function parseMemberSearchFieldsSetting(
  settings: SettingsArray,
): BasicSearchField[] {
  return parseSearchFieldsSetting(
    settings,
    SOCIETY_SETTING_TYPE.MEMBER_SEARCH_FIELDS,
    MEMBER_SETTING_FIELD_TO_FIELD_OPTION,
    (field) => ({ label: field, value: field }),
  );
}

// Helper to build EBV field options from society settings
export function buildEbvFieldOptions(
  ebvConfig: EbvConfigItem[],
  opts?: { includePercentileFields?: boolean },
): { label: string; value: string }[] {
  const includePercentileFields = opts?.includePercentileFields ?? true;
  const options: { label: string; value: string }[] = [];

  // Filter config items where display > 0
  const visibleConfigs = ebvConfig.filter(isDisplayableEbvConfig);

  for (const config of visibleConfigs) {
    const displayLabel = config.label.replace(/\^/g, ' ');
    const fieldNumber = config.field.replace('ebv', '');

    // EBV/Index option
    options.push({
      label: displayLabel,
      value: `ebv${fieldNumber}`,
    });

    // EBV Accuracy option
    options.push({
      label: `${displayLabel} Accuracy`,
      value: `ebv${fieldNumber}Acc`,
    });

    // EBV Percentile option (BREEDPLAN societies only)
    if (includePercentileFields) {
      options.push({
        label: `${displayLabel} Percentile`,
        value: `ebv${fieldNumber}Percentile`,
      });
    }
  }

  return options;
}

export type DynamicFieldOption = {
  label: string;
  value: string;
  category?: string;
  /**
   * Optional preset-style lookup choices (currently used for member
   * MEMBER_SEARCH_FIELDS entries like ms_select_*, ms_grp_select_*,
   * *_radio). When present, the consuming UI should render a single
   * description-dropdown segment instead of operator + value: the
   * society wired the field to a fixed set of named conditions, each
   * of which expands to a real underlying filter via `i6LookupString`.
   */
  lookups?: AnimalSearchFieldLookup[];
};

const MEMBER_FILTER_FIELD_LABELS: Record<string, string> = {
  [MemberFilterField.MEMBER_ID]: 'Member Id',
  [MemberFilterField.MEMBER_IDENT]: 'Member Ident',
  [MemberFilterField.PREFIX]: 'Herd Prefix',
  [MemberFilterField.MEMB_NAME]: 'Name',
  [MemberFilterField.ADDRESS1]: 'Address 1',
  [MemberFilterField.ADDRESS2]: 'Address 2',
  [MemberFilterField.ADDRESS3]: 'Address 3',
  [MemberFilterField.POSTCODE]: 'Postcode',
  [MemberFilterField.SORTNAME]: 'Sort Name',
  [MemberFilterField.PHONE_DETAILS]: 'Phone',
  [MemberFilterField.EMAIL]: 'Email',
  [MemberFilterField.HOMEPAGE]: 'Homepage',
  [MemberFilterField.MEMBER_TYPE]: 'Membership Type',
  [MemberFilterField.MEMBER_NOTES]: 'Notes',
  [MemberFilterField.HERD_SIZE]: 'Herd Size',
  [MemberFilterField.HERD_SIZE_DESC]: 'Herd Size Description',
  [MemberFilterField.MEMB_STATUS]: 'Status',
  [MemberFilterField.RENEWAL_DATE]: 'Renewal Date',
};

const INTERNAL_MEMBER_FIELDS = new Set([
  MemberFilterField.SOCIETY_ID,
  MemberFilterField.SOCIETY_CODE,
  MemberFilterField.DETAILS_AVAIL_FLAG,
  MemberFilterField.BREEDPLAN_FLAG,
  MemberFilterField.MEMB_AUTH,
  MemberFilterField.ITEM_KEY,
  MemberFilterField.RENEWAL_FEE,
  MemberFilterField.MAP_DETAILS,
]);

export function getAllFieldOptionsForSearchType(
  searchType: string,
  _fields: BasicSearchField[],
  ebvConfig: EbvConfigItem[],
  excludeFields?: Set<string>,
  /**
   * Society settings used to apply
   * society-customised labels and to filter out unconfigured
   * SOC_ME_FIELD{n} entries — same shape as how animal hides
   * unconfigured SocietyAnimalField columns via parseAnimalSearchFieldsConfig.
   */
  settings?: SettingsArray,
  ebvLabel?: string,
  includePercentileFields = true,
): DynamicFieldOption[] {
  const isAnimalType =
    searchType === SEARCH_TYPE.ANIMAL ||
    searchType === SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS ||
    searchType === SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS;

  const exclude = excludeFields ?? new Set<string>();

  if (isAnimalType) {
    const configured = getAnimalFilterFieldOptionsFromSearchSettings(settings);
    const animalOptions: DynamicFieldOption[] = configured
      .filter((f) => !exclude.has(f.value))
      .map((f) => ({
        ...f,
        label: f.label,
        value: f.value,
        category: 'Animal Details',
      }));

    const ebvOptions: DynamicFieldOption[] = buildEbvFieldOptions(ebvConfig, {
      includePercentileFields,
    })
      .filter((o) => !exclude.has(o.value))
      .map((o) => ({
        ...o,
        category: formatEbvAndIndexesTabLabel(ebvLabel ?? 'EBV'),
      }));

    return [...animalOptions, ...ebvOptions];
  }

  if (searchType === SEARCH_TYPE.MEMBERS) {
    // Source-of-truth: MEMBER_SEARCH_FIELDS (`SocietySettingType = 19`).
    // It tells us exactly which fields the society wants exposed for
    // member search and what to call them — same as how
    // ANIMAL_SEARCH_FIELDS feeds the animal basic-search field list.
    // When the setting is absent we fall back to a sensible default
    // (standard scalar fields, no SOC_ME_FIELD{n} placeholders).
    const fromSearchSetting =
      getMemberFilterFieldOptionsFromSearchSettings(settings);

    if (fromSearchSetting !== null) {
      return fromSearchSetting
        .filter((opt) => !exclude.has(opt.value))
        .map((opt) => ({
          ...opt,
          category: opt.lookups?.length
            ? 'Member Search Selections'
            : 'Member Details',
        }));
    }

    return Object.values(MemberFilterField)
      .filter((v) => !INTERNAL_MEMBER_FIELDS.has(v) && !exclude.has(v))
      .filter((v) => !/^SOC_ME_FIELD\d+$/.test(v))
      .map((v) => ({
        label: MEMBER_FILTER_FIELD_LABELS[v] ?? v.replace(/_/g, ' '),
        value: v,
        category: 'Member Details',
      }));
  }

  return [];
}
